import React, { useState, useEffect, useRef, useCallback } from "react";
import { API_BASE_URL } from "./constants/adminConstants";
import "./css/adminStyles.css";

function AdminNotificationPanel({ user }) {
  const [notifications, setNotifications] = useState([]);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [showDeleteAllOverlay, setShowDeleteAllOverlay] = useState(false);
  const searchRef = useRef(null);
  useEffect(() => {
    if (searchOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [searchOpen]);

  const rolePath = user?.role?.toLowerCase() || "admin";

  const fetchNotifications = useCallback(async () => {
    const token = sessionStorage.getItem("tvc_token");
    if (!token) return;

    try {
      const filter = showHistory ? "history" : "unread";
      const res = await fetch(`${API_BASE_URL}/${rolePath}/notifications?filter=${filter}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();

      if (res.ok) {
        setNotifications(data.notifications || []);
      }
    } catch (err) {
      console.error("Fetch notifications error:", err);
    } finally {
      setLoading(false);
    }
  }, [user, showHistory]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleMarkRead = async (id) => {
    const token = sessionStorage.getItem("tvc_token");
    try {
      await fetch(`${API_BASE_URL}/${rolePath}/notifications/${id}/read`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: 1 } : n))
      );
    } catch (err) {
      console.error("Mark read error:", err);
    }
  };

  const handleDelete = async (id) => {
    const token = sessionStorage.getItem("tvc_token");
    try {
      const res = await fetch(`${API_BASE_URL}/${rolePath}/notifications/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setNotifications((prev) => prev.filter((n) => n.id !== id));
      }
    } catch (err) {
      console.error("Delete notification error:", err);
    }
  };

  const handleDeleteAll = async () => {
    const token = sessionStorage.getItem("tvc_token");
    try {
      const res = await fetch(`${API_BASE_URL}/${rolePath}/notifications`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setNotifications([]);
        setShowDeleteAllOverlay(false);
      }
    } catch (err) {
      console.error("Delete all notifications error:", err);
    }
  };

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredNotifications(notifications);
      return;
    }
    const q = searchQuery.trim().toLowerCase();
    setFilteredNotifications(
      notifications.filter(
        (n) =>
          (n.title || "").toLowerCase().includes(q) ||
          (n.message || "").toLowerCase().includes(q)
      )
    );
  }, [searchQuery, notifications]);

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleString();
  };

  if (loading) {
    return <div className="AdminEmptyState">Loading notifications...</div>;
  }

  const displayed = searchQuery.trim() ? filteredNotifications : notifications;

  return (
    <div className="AdminCard">
      <div className="AdminCardHeader">
        <h3>Notifications <span className="AdminCountBadge">{filteredNotifications.length}</span></h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div className={`AdminPillSearchWrap ${searchOpen ? 'is-open' : ''}`}>
            <span className="AdminPillSearchIcon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
            </span>
            <input
              ref={searchRef}
              type="text"
              className="AdminPillSearch"
              placeholder="Search title, message..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
               onBlur={() => { setSearchQuery(""); setTimeout(() => setSearchOpen(false), 200) }}
            />
            {!searchOpen && (
              <button className="AdminPillSearchOverlay" onClick={() => setSearchOpen(true)} type="button" aria-label="Search" />
            )}
          </div>
          {displayed.length > 0 && (
            <button className="AdminButton AdminButtonSmall AdminButtonSecondary" onClick={() => setShowDeleteAllOverlay(true)}>
              Delete All
            </button>
          )}
          <button
            className="AdminPillSelect"
            style={{ cursor: "pointer", minWidth: 100, textAlign: "center" }}
            onClick={() => setShowHistory((prev) => !prev)}
          >
            {showHistory ? "Show Unread" : "History"}
          </button>
        </div>
      </div>

      {displayed.length === 0 ? (
        <div className="AdminEmptyState">
          {searchQuery.trim() ? `No results found for "${searchQuery}"` : (showHistory ? "No read notifications yet." : "No unread notifications.")}
        </div>
      ) : (
        displayed.map((n) => (
          <div
            key={n.id}
            className="AdminNotificationItem"
            style={{ cursor: n.read ? "default" : "pointer", display:"flex", alignItems:"center", gap:8 }}
            onClick={() => !n.read && handleMarkRead(n.id)}
          >
            {!n.read && <span className="AdminUnreadIndicator" />}
            <div style={{ flex: 1, minWidth:0 }}>
              <p className="AdminNotificationTitle">{n.title}</p>
              {n.message && <p className="AdminNotificationMessage">{n.message}</p>}
              <p className="AdminNotificationTime">{formatDate(n.createdAt)}</p>
            </div>
            <button
              className="AdminButton AdminButtonSmall"
              style={{ background:"#dc2626", flexShrink:0, whiteSpace:"nowrap" }}
              onClick={(e) => { e.stopPropagation(); handleDelete(n.id); }}
              title="Delete"
            >&#10005; Delete</button>
          </div>
        ))
      )}

      {showDeleteAllOverlay && (
        <div className="AdminOverlay" onClick={() => setShowDeleteAllOverlay(false)}>
          <div className="AdminOverlayCard" onClick={(e) => e.stopPropagation()}>
            <div className="AdminOverlayHeader">
              <h3>Delete All Notifications</h3>
              <button className="AdminIconBtn" onClick={() => setShowDeleteAllOverlay(false)} title="Close" style={{fontSize:28,width:36,height:36}}>&times;</button>
            </div>
            <div style={{padding:20,textAlign:"center"}}>
              <p style={{color:"#64748b",fontSize:15,margin:"0 0 20px",fontFamily:"'DM Sans',sans-serif"}}>Are you sure you want to delete all notifications?</p>
              <div className="AdminFlexRowGap8" style={{justifyContent:"center"}}>
                <button className="AdminButton AdminButtonSecondary" onClick={() => setShowDeleteAllOverlay(false)}>Cancel</button>
                <button className="AdminButton" style={{background:"#dc2626"}} onClick={handleDeleteAll}>Delete All</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminNotificationPanel;
